import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputBase,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { FORM_VALIDATIONS } from "constants/formValidation";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "store/appHooks";
import InputWrapper from "./input-wrapper";
import { Add, Close } from "@mui/icons-material";
import SavedAddress from "components/saved-address";
import { createUserCards, updateUserCards } from "slices/cardReducer";
import useQuery from "utils/router.util";
import { useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useFormik } from "formik";

const stripePromise = loadStripe(
  process.env.REACT_APP_PUBLIC_STRIPE_LIVE_KEY as string
);

const AddNewCardForm = ({ onSuccess }: { onSuccess?: any }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [reloadComponent, setReloadComponent] = useState(true);
  const { cards }: any = useAppSelector((state) => state.cardReducer);
  const query = useQuery();

  const [editData, setEditData] = useState<any>(null);

  useEffect(() => {
    if (query.get("id")) {
      setEditData(cards.find((r: any) => r.id === Number(query.get("id"))));

      setReloadComponent(false);
      setTimeout(() => setReloadComponent(true), 50);
    } else {
      setEditData(null);
    }
  }, [cards, query]);

  return (
    <Box
      sx={{
        height: "100%",
        py: {
          md: "25px",
          lg: "25px",
          xl: "25px",
          sm: "0",
          xs: "0",
        },
      }}
    >
      <Box
        sx={{
          p: {
            md: "20px 30px 0 20px",
            lg: "20px 30px 0 20px",
            xl: "20px 30px 0 20px",
            sm: "20px 30px 0 20px",
            xs: "20px 30px 0 20px",
          },
        }}
      >
        <Typography
          sx={{
            color: "#323232",
            fontWeight: 600,
            fontSize: "20px",
            lineHeight: "22px",
          }}
        >
          {query.get("id") ? `Edit Card` : `Add new`}
        </Typography>
        {reloadComponent && (
          <Elements stripe={stripePromise}>
            <InjectedPaymentForm
              onToken={(token: string, name: string, addressId: number) => {
                if (!editData) {
                  dispatch(
                    createUserCards({
                      stripe_card_token: token,
                      card_holder_name: name,
                      address_id: addressId,
                    })
                  );
                  if (query.get("goBack")) {
                    navigate("/payment");
                  }
                } else {
                  dispatch(
                    updateUserCards({
                      id: editData?.id,
                      card_holder_name: name,
                      address_id: addressId,
                    })
                  );
                  navigate("/saved-cards");

                  if (query.get("goBack")) {
                    navigate("/payment");
                  }
                }
                setReloadComponent(false);
                setTimeout(() => setReloadComponent(true), 100);
                onSuccess && onSuccess();
              }}
              onClose={() => {}}
              existing={editData}
            />
          </Elements>
        )}
      </Box>
    </Box>
  );
};

const ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: "15px",
      fontWeight: 400,
      fontFamily: "Inter",
      color: "#000",
      "::placeholder": {
        color: "#A2A2A2",
      },
    },
    invalid: {
      color: "#9e2146",
    },
  },
};

const InjectedPaymentForm = ({
  onToken,
  onClose,
  existing,
}: {
  onToken: any;
  onClose?: any | null;
  existing: any;
}) => {
  const { addressReducer } = useAppSelector((state) => state);
  const { savedAddress } = addressReducer;
  const [errorIncard, setErrorInCard] = useState("");

  const formik: any = useFormik({
    initialValues: {
      cardNumber: existing?.last_4 || "",
      cardholderName: existing?.card_holder_name || "",
      address: existing?.listing_address?.id || "",
      cardExpiry: existing
        ? `${existing.expiry_month} / ${existing.expiry_year}`
        : "",
      cardCVV: existing ? `XXX` : "",
    },
    validationSchema: Yup.object().shape({
      cardholderName: Yup.string()
        .required("Card Holder name is required")
        .matches(/^[a-zA-Z ]*$/, "Only alphabet is allowed")
        .typeError("Please enter valid card number"),
      address: Yup.number().required("Address is required"),
    }),
    onSubmit: (values) => {
      createPayment(values);
    },
  });
  const element = useElements();
  const stripe = useStripe();
  const [addNewAddress, setAddNewAddress] = useState(false);

  useEffect(() => {
    const addressDefault = savedAddress.find(
      (add: any) => add?.is_default === true
    );
    if (addressDefault && !existing)
      formik.setFieldValue("address", addressDefault.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedAddress]);

  const createPayment = async (data: any) => {
    if (!stripe || !element) {
      return;
    }
    setErrorInCard(""); //clear error
    const addressData = savedAddress.find(
      (add: any) => add.id === data.address
    );

    const cardNumber: any = element.getElement(CardNumberElement);

    if (!existing) {
      const { token, error }: any = await stripe.createToken(cardNumber, {
        name: data.cardholderName,
        address_line1: addressData.address_line_1,
        address_country: addressData.country,
        address_state: addressData.state,
        address_zip: addressData.zipcode,
      });
      if (!error) onToken(token.id, data.cardholderName, addressData.id);
      else setErrorInCard(error?.message || "Wrong card details");
    } else {
      onToken("edit", data.cardholderName, addressData.id);
    }
    onClose();
    // reset();
  };

  const CustomSelect = ({ inputRef, ...props }: any) => (
    <InputBase
      {...props}
      inputRef={inputRef}
      sx={{
        border: "1px solid #E2E2E2",
        height: "56px",
        borderRadius: "10px",

        pl: "20px",
      }}
    />
  );

  return (
    <Box mt="25px">
      <form onSubmit={formik.handleSubmit} id="paymentForm">
        {errorIncard && <Alert severity="error">{errorIncard}</Alert>}
        <Grid container spacing={2} mt={1}>
          <Grid item md={12} lg={12} xl={12} sm={12} xs={12}>
            {!existing && (
              <InputWrapper>
                <CardNumberElement
                  // onChange={handleCardNumberChange}
                  // onBlur={(e: any) => {
                  //   formik.setTouched({ ...formik.touched, cardNumber: true });
                  // }}
                  id="cardNumber"
                  options={ELEMENT_OPTIONS}
                />
              </InputWrapper>
            )}
            {existing && (
              <InputWrapper>
                <InputBase
                  placeholder="Last 4 digit"
                  fullWidth
                  disabled
                  // error={!!error}
                  value={`XXXX XXXX XXXX ${formik.values?.cardNumber}`}
                  sx={{
                    height: "10px",
                    fontSize: "15px",
                    "::placeholder": {
                      fontWeight: 500,
                    },
                  }}
                />
              </InputWrapper>
            )}
          </Grid>
          <Grid item md={12} lg={12} xl={12} sm={12} xs={12}>
            <>
              <InputWrapper>
                <InputBase
                  placeholder="Name on Card"
                  fullWidth
                  onChange={(e) => {
                    formik.setFieldValue(
                      "cardholderName",
                      e.target.value,
                      false
                    );
                  }}
                  onBlur={(e: any) => {
                    formik.setTouched({
                      ...formik.touched,
                      cardholderName: true,
                    });
                  }}
                  error={!!formik.errors.cardholderName}
                  value={formik.values.cardholderName}
                  sx={{
                    height: "10px",
                    fontSize: "15px",
                    "::placeholder": {
                      fontWeight: 500,
                    },
                  }}
                />
              </InputWrapper>
              {formik.errors?.cardholderName &&
                formik.touched.cardholderName && (
                  <Typography variant="caption" color="error">
                    {formik.errors?.cardholderName}
                  </Typography>
                )}
            </>
          </Grid>
          <Grid item md={9} lg={9} xl={9} sm={8} xs={8}>
            <Box>
              {!existing && (
                <InputWrapper>
                  <CardExpiryElement
                    // onChange={handleCardExpiryChange}
                    id="cardExpiry"
                    options={ELEMENT_OPTIONS}
                    // onBlur={(e: any) => {
                    //   formik.setTouched({
                    //     ...formik.touched,
                    //     cardExpiry: true,
                    //   });
                    // }}
                  />
                </InputWrapper>
              )}

              {existing && (
                <InputWrapper>
                  <InputBase
                    placeholder="MM / YY"
                    fullWidth
                    disabled
                    value={formik?.values?.cardExpiry}
                    sx={{
                      height: "10px",
                      fontSize: "15px",
                      "::placeholder": {
                        fontWeight: 500,
                      },
                    }}
                  />
                </InputWrapper>
              )}
              {formik?.errors?.cardExpiry && formik?.touched?.cardExpiry && (
                <Typography variant="caption" color="error">
                  {formik?.errors?.cardExpiry || ""}
                </Typography>
              )}
            </Box>
          </Grid>
          <Grid item md={3} lg={3} xl={3} sm={4} xs={4}>
            <Box>
              {!existing && (
                <InputWrapper>
                  <CardCvcElement
                    // onChange={handleCardCVVChange}
                    id="cardCVV"
                    options={ELEMENT_OPTIONS}
                    // onBlur={(e: any) => {
                    //   formik.setTouched({
                    //     ...formik.touched,
                    //     cardCVV: true,
                    //   });
                    // }}
                  />
                </InputWrapper>
              )}
              {existing && (
                <InputWrapper>
                  <InputBase
                    placeholder="CVV"
                    fullWidth
                    disabled
                    value="XXX"
                    sx={{
                      height: "10px",
                      fontSize: "15px",
                      "::placeholder": {
                        fontWeight: 500,
                      },
                    }}
                  />
                </InputWrapper>
              )}
              {formik?.errors?.cardCVV && formik?.touched?.cardCVV && (
                <Typography variant="caption" color="error">
                  {formik?.errors?.cardCVV || ""}
                </Typography>
              )}
            </Box>
          </Grid>
          <Grid item md={12} lg={12} xl={12} sm={12} xs={12}>
            {savedAddress.length > 0 && (
              <>
                <Typography fontWeight={700} mb={1}>
                  Billing Address
                </Typography>

                <Select
                  name="address"
                  fullWidth
                  onChange={formik.handleChange}
                  value={formik.values.address}
                  defaultValue={formik.values.address}
                  input={<CustomSelect />}
                  onBlur={() =>
                    formik.setTouched({
                      ...formik.touched,
                      address: true,
                    })
                  }
                >
                  {savedAddress.map(
                    (
                      add: {
                        address_line_1: string;
                        id: number;
                        address_line_2: string;
                        city: string;
                        state: string;
                        zipcode: string;
                      },
                      i: number
                    ) => (
                      <MenuItem key={i} value={add.id}>
                        {add.address_line_1}
                        {` ${
                          add.address_line_2 ? ` ,${add.address_line_2}` : ``
                        }`}
                        {add.city},{add.state},{add.zipcode}
                      </MenuItem>
                    )
                  )}
                </Select>
                {formik?.errors?.address && formik?.touched?.address && (
                  <Typography variant="caption" color="error">
                    {formik?.errors?.address || ""}
                  </Typography>
                )}
              </>
            )}
            <Button startIcon={<Add />} onClick={() => setAddNewAddress(true)}>
              Add new address
            </Button>
          </Grid>
        </Grid>
        <Box>
          <Box
            display="flex"
            justifyContent="flex-end"
            gap={1}
            sx={{ mt: { md: 5, lg: 5, xl: 5, sm: 2, xs: 2 }, mb: 2 }}
          >
            <Button
              sx={{
                height: {
                  md: "56px",
                  lg: "56px",
                  xl: "56px",
                  sm: "40px",
                  xs: "40px",
                },
                borderRadius: "10px",
                minWidth: "184px",
              }}
              variant="contained"
              type="submit"
              disabled={!formik.isValid}
            >
              Save Card
            </Button>
          </Box>
        </Box>
      </form>

      {addNewAddress && (
        <Dialog
          open
          maxWidth="lg"
          fullWidth
          onClose={() => setAddNewAddress(false)}
          sx={{ padding: 0 }}
        >
          <DialogTitle
            sx={{
              padding: 2,
              display: "flex",
              position: "absolute",
              right: 0,
            }}
          >
            <IconButton onClick={() => setAddNewAddress(false)}>
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ padding: 0 }}>
            <SavedAddress />
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

export default AddNewCardForm;
